import { useMemo, useState } from 'react';
import { PackageMinus, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { money } from '../outpatient/ui';
import { useBorrowHospitals, useBorrowStock, useCreateBorrowIssue } from './useBorrow';
import { ErrorBanner, SETTLEMENT_LABEL, addDaysYmd, expiryLabel, localDate, useDebounced } from './ui';
import type { BorrowIssue, BorrowSettings, BorrowStockBatch, SettlementType } from './types';

/**
 * Create Issue Record — hospital, item/batch, quantity, value, dates,
 * settlement type, reason.
 *
 * The batch picker only offers what can legally leave the shelf right now: in
 * stock and not expired, at this branch (GET /pharmacy-borrow/stock). The
 * server re-checks both under a row lock, so the picker is a convenience, not
 * the guard against negative stock.
 *
 * The hospital's policy (Admin Panel → Pharmacy → Medicine Borrow & Return) shapes the
 * form — defaults, allowed settlement types, value basis, limits, required
 * contact fields, preset reasons — and the server enforces the same rules.
 */
export default function IssueDialog({ settings, onClose, onCreated }: {
  settings: BorrowSettings;
  onClose: () => void;
  onCreated: (issue: BorrowIssue) => void;
}) {
  const [hospital, setHospital] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [search, setSearch] = useState('');
  const [batch, setBatch] = useState<BorrowStockBatch | null>(null);
  const [quantity, setQuantity] = useState('');
  const [value, setValue] = useState('');
  const [valueTouched, setValueTouched] = useState(false);
  const [issueDate, setIssueDate] = useState(localDate());
  const [expectedDate, setExpectedDate] = useState(localDate(settings.default_return_days));
  const [settlement, setSettlement] = useState<SettlementType>(settings.default_settlement_type);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');

  const debouncedSearch = useDebounced(search.trim());
  const stockQuery = useBorrowStock(debouncedSearch);
  const hospitalsQuery = useBorrowHospitals('');
  const create = useCreateBorrowIssue();

  const qty = parseInt(quantity, 10);
  const qtyValid = Number.isInteger(qty) && qty > 0 && String(qty) === quantity.trim();
  // Value follows the policy's basis × qty (purchase price falls back to MRP,
  // as on the server) until the operator types their own figure — if allowed.
  const cost = batch ? parseFloat(batch.purchase_price ?? '') : NaN;
  const unit = batch ? (settings.value_basis === 'purchase_price' && cost > 0 ? cost : parseFloat(batch.mrp)) : 0;
  const suggestedValue = batch && qtyValid ? Math.round(unit * qty * 100) / 100 : null;
  const canOverride = settings.allow_value_override;
  const effectiveValue = canOverride && valueTouched ? value : suggestedValue != null ? suggestedValue.toFixed(2) : '';
  const maxExpected = settings.max_return_days != null ? addDaysYmd(issueDate, settings.max_return_days) : undefined;
  const settlementOptions = ([
    settings.allow_item_return ? 'item_return' : null,
    settings.allow_payment ? 'payment' : null,
  ].filter(Boolean) as SettlementType[]).map((v) => ({ value: v, label: SETTLEMENT_LABEL[v] }));

  const pickHospital = (name: string) => {
    setHospital(name);
    const known = hospitalsQuery.data?.find((h) => h.hospital_name.toLowerCase() === name.trim().toLowerCase());
    if (known) {
      if (!contactPerson && known.contact_person) setContactPerson(known.contact_person);
      if (!contactNumber && known.contact_number) setContactNumber(known.contact_number);
    }
  };

  const stock = useMemo(() => stockQuery.data ?? [], [stockQuery.data]);

  const submit = () => {
    if (!hospital.trim()) return setFormError('Enter the recipient hospital.');
    if (settings.require_contact_person && !contactPerson.trim()) return setFormError('Enter the contact person — required by hospital policy.');
    if (settings.require_contact_number && !contactNumber.trim()) return setFormError('Enter the contact number — required by hospital policy.');
    if (!batch) return setFormError('Select the medicine batch being issued.');
    if (!qtyValid) return setFormError('Quantity must be a whole number greater than zero.');
    if (qty > batch.quantity_available) return setFormError(`Only ${batch.quantity_available} in stock for batch ${batch.batch_number}.`);
    if (settings.max_quantity_per_issue != null && qty > settings.max_quantity_per_issue) {
      return setFormError(`Hospital policy allows at most ${settings.max_quantity_per_issue} unit(s) per issue.`);
    }
    const itemValue = parseFloat(effectiveValue);
    if (!Number.isFinite(itemValue) || itemValue < 0) return setFormError('Item value must be zero or more.');
    if (settlement === 'payment' && itemValue <= 0) return setFormError('A record settled by payment needs a value above zero.');
    if (!expectedDate) return setFormError('Enter the expected return / settlement date.');
    if (expectedDate < issueDate) return setFormError('Expected date cannot be before the issue date.');
    if (maxExpected && expectedDate > maxExpected) {
      return setFormError(`Hospital policy allows at most ${settings.max_return_days} day(s) until return (by ${maxExpected}).`);
    }
    if (!reason.trim()) return setFormError('Give a reason for the issue.');
    setFormError('');
    create.mutate(
      {
        hospital_name: hospital.trim(),
        contact_person: contactPerson.trim() || undefined,
        contact_number: contactNumber.trim() || undefined,
        inventory_id: batch.inventory_id,
        quantity: qty,
        item_value: itemValue,
        issue_date: issueDate,
        expected_return_date: expectedDate,
        settlement_type: settlement,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      },
      { onSuccess: (issue) => onCreated(issue) }
    );
  };

  const apiError = create.error instanceof ApiError ? create.error.message : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-[680px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageMinus size={17} /> Issue medicine to another hospital</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Recipient ─────────────────────────────────── */}
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Hospital name" required className="sm:col-span-3">
              <Input
                list="borrow-hospitals"
                value={hospital}
                placeholder="e.g. Nearby Hospital"
                onChange={(e) => pickHospital(e.target.value)}
              />
              <datalist id="borrow-hospitals">
                {(hospitalsQuery.data ?? []).map((h) => <option key={h.hospital_name} value={h.hospital_name} />)}
              </datalist>
            </FormField>
            <FormField label="Contact person" required={settings.require_contact_person} className="sm:col-span-2">
              <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
            </FormField>
            <FormField label="Contact number" required={settings.require_contact_number}>
              <Input type="tel" value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} />
            </FormField>
          </div>

          {/* ── Item & batch ──────────────────────────────── */}
          <div>
            <div className="lbl">Item / medicine &amp; batch <span className="text-danger">*</span></div>
            {batch ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-teal bg-accent px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold">{batch.generic_name} {batch.strength}{batch.brand_name ? ` (${batch.brand_name})` : ''}</div>
                  <div className="text-[11.5px] text-ink-muted">
                    Batch <span className="font-mono">{batch.batch_number}</span> · Exp {expiryLabel(batch.expiry_date)} · {batch.quantity_available} in stock · MRP {money(batch.mrp)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setBatch(null)}>Change</Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
                  <Input className="pl-8" value={search} placeholder="Search medicine, brand or batch" onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="mt-1.5 max-h-48 space-y-1 overflow-y-auto">
                  {stockQuery.isLoading ? (
                    <div className="px-2 py-3 text-[12px] text-ink-muted">Searching…</div>
                  ) : stock.length === 0 ? (
                    <div className="px-2 py-3 text-[12px] text-ink-muted">No in-stock, unexpired batch matches.</div>
                  ) : (
                    stock.map((s) => (
                      <button
                        key={s.inventory_id}
                        type="button"
                        onClick={() => setBatch(s)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-md border border-border px-2.5 py-1.5 text-left',
                          'hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25'
                        )}
                      >
                        <span className="min-w-0 truncate text-[12.5px]">
                          <span className="font-semibold">{s.generic_name}</span> {s.strength}
                          {s.brand_name && <span className="text-ink-muted"> · {s.brand_name}</span>}
                        </span>
                        <span className="shrink-0 text-[11px] text-ink-muted">
                          <span className="font-mono">{s.batch_number}</span> · {expiryLabel(s.expiry_date)} · {s.quantity_available} left
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <FormField label="Quantity" required>
              <Input type="number" min={1} max={Math.min(batch?.quantity_available ?? Infinity, settings.max_quantity_per_issue ?? Infinity)} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </FormField>
            <FormField label="Item value (₹)" required>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={effectiveValue}
                disabled={!canOverride}
                title={canOverride ? undefined : 'Fixed by hospital policy'}
                onChange={(e) => { setValueTouched(true); setValue(e.target.value); }}
              />
            </FormField>
            <FormField label="Issue date" required>
              <Input type="date" max={localDate()} value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </FormField>
            <FormField label="Expected return" required>
              <Input type="date" min={issueDate} max={maxExpected} value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            </FormField>
          </div>
          {suggestedValue != null && canOverride && valueTouched && (
            <button type="button" className="-mt-2 text-[11px] text-teal hover:underline" onClick={() => setValueTouched(false)}>
              Use {settings.value_basis === 'purchase_price' ? 'purchase-price' : 'MRP'} value ({money(suggestedValue)})
            </button>
          )}

          <div>
            <div className="lbl">Settlement type</div>
            <SegmentedControl<SettlementType>
              value={settlement}
              onChange={setSettlement}
              options={settlementOptions}
            />
            <p className="mt-1 text-[11px] text-ink-muted">
              {settlement === 'item_return'
                ? 'Closes when the same medicine comes back in full and is put back on the shelf.'
                : 'Closes when the item value has been paid in full.'}
            </p>
          </div>

          <FormField label="Reason" required>
            <Input value={reason} placeholder="e.g. emergency shortage at recipient hospital" onChange={(e) => setReason(e.target.value)} />
          </FormField>
          {settings.reason_presets.length > 0 && (
            <div className="-mt-2 flex flex-wrap gap-1.5">
              {settings.reason_presets.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-[11.5px] transition-colors',
                    reason === r ? 'border-teal bg-accent text-teal' : 'border-border text-ink-muted hover:bg-muted'
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
          <FormField label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>

          <ErrorBanner message={formError || apiError} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={create.isPending} onClick={submit}>Issue &amp; reduce stock</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
